import { useId, useState } from 'react';
import type { Source } from '@mg/shared';
import { Modal } from '../../components/ui/Modal';
import { Button } from '../../components/ui/Button';
import { Input, Label } from '../../components/ui/Field';
import { isDeleteConfirmed } from './sourceSharing';

/**
 * "Delete source", unlocked by typing "delete". Open while `source` is set. Render it with
 * `key={source?.id}` so what was typed for one source never carries over to the next.
 */
export function DeleteSourceModal({
  source,
  busy,
  error,
  onConfirm,
  onClose,
}: {
  source: Source | undefined;
  busy: boolean;
  /** Why the last attempt failed, shown in the dialog so the user is not left guessing. */
  error: string | null;
  onConfirm: () => void;
  onClose: () => void;
}) {
  const inputId = useId();
  const formId = useId();
  const nameId = useId();
  const [typed, setTyped] = useState('');
  const confirmed = isDeleteConfirmed(typed);

  return (
    <Modal
      open={Boolean(source)}
      onClose={onClose}
      title="Delete source"
      closeDisabled={busy}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          {/* The form's submit button, so Enter in the box submits — and, like a click, only once
              the typed word unlocks it: a disabled default button blocks implicit submission. */}
          <Button
            type="submit"
            form={formId}
            variant="danger"
            disabled={!confirmed}
            loading={busy}
          >
            Delete source
          </Button>
        </>
      }
    >
      <form
        id={formId}
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          if (confirmed && !busy) onConfirm();
        }}
      >
        <ul
          id={nameId}
          className="rounded-lg border border-line px-3 py-2 text-sm font-medium text-ink"
        >
          <li className="break-words">{source?.name}</li>
        </ul>
        <div>
          <Label htmlFor={inputId}>Type delete to confirm</Label>
          <Input
            id={inputId}
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            // Focus lands here, past the name above — so the name is read out with the field.
            aria-describedby={nameId}
            autoFocus
            autoComplete="off"
            spellCheck={false}
            // Read-only, not disabled, while the request runs: disabling the focused field drops
            // focus out of the dialog, and it would not come back if the delete failed.
            readOnly={busy}
          />
        </div>
        {error && (
          <p role="alert" className="text-sm text-red-600 dark:text-error-dark">
            {error}
          </p>
        )}
      </form>
    </Modal>
  );
}
